import { HttpErrorResponse, HttpInterceptorFn } from "@angular/common/http";
import { PLATFORM_ID, inject } from "@angular/core";
import { isPlatformServer } from "@angular/common";
import { TimeoutError, throwError } from "rxjs";
import { catchError, timeout } from "rxjs/operators";

/** Max time a server-side (SSR) HttpClient call may run before it is aborted. */
export const SSR_HTTP_TIMEOUT_MS = 10000;

/**
 * Bounds server-side HttpClient calls during SSR. Angular's render waits for
 * every in-flight HTTP call to settle before returning HTML, and these calls
 * had no timeout — so a slow/hung backend made renders wait forever. The lone
 * fork-mode render process then stopped accepting connections, nginx reported
 * "no live upstreams", and the public site went down until a manual restart
 * (outage 2026-06-19). A bounded call fails fast instead, letting the render
 * finish (degraded) and freeing the worker.
 *
 * Scoped to the server: client-side requests pass through untouched so long
 * uploads/downloads (the /api route allows 500M bodies) are never cut off.
 * A timeout is surfaced as a 504 HttpErrorResponse so existing
 * HttpErrorResponse-based error handling treats it like any upstream failure.
 */
export const ssrTimeoutInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isPlatformServer(inject(PLATFORM_ID))) return next(req);

  return next(req).pipe(
    timeout(SSR_HTTP_TIMEOUT_MS),
    catchError((err) => {
      if (err instanceof TimeoutError) {
        return throwError(
          () =>
            new HttpErrorResponse({
              url: req.url,
              status: 504,
              statusText: `SSR upstream timeout after ${SSR_HTTP_TIMEOUT_MS}ms`,
            }),
        );
      }
      return throwError(() => err);
    }),
  );
};
